import React, { useEffect, useState } from "react";
import { SimpleGrid, Spinner, Text, VStack, useToast } from "@chakra-ui/react";
import { useAuth } from "../../auth/AuthContext";
import FinancePageShell from "./common/FinancePageShell";
import FinanceStatCard from "./common/FinanceStatCard";
import FinanceSectionCard from "./common/FinanceSectionCard";
import { financeApi } from "./financeApi";
import { formatMoney } from "./common/formatters";

const SellerFinanceDashboard: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<any>(null);

  useEffect(() => {
    const run = async () => {
      if (!user?.uid) {
        setPayload(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const res = await financeApi.getSellerFinance(user.uid);
        setPayload(res?.data || null);
      } catch (error: any) {
        console.error("Failed to load seller finance:", error);

        toast({
          status: "error",
          title: "Failed to load seller finance",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "Please try again.",
          duration: 4000,
          isClosable: true,
        });

        setPayload(null);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [toast, user?.uid]);

  if (loading) {
    return (
      <FinancePageShell title="My Seller Finance">
        <VStack py={10}>
          <Spinner />
          <Text>Loading seller finance...</Text>
        </VStack>
      </FinancePageShell>
    );
  }

  if (!user?.uid) {
    return (
      <FinancePageShell
        title="My Seller Finance"
        subtitle="Track what has sold, what is eligible, and what has already been paid out."
      >
        <FinanceSectionCard title="Sign in required">
          <Text>Please sign in to view your seller finance dashboard.</Text>
        </FinanceSectionCard>
      </FinancePageShell>
    );
  }

  if (!payload) {
    return (
      <FinancePageShell
        title="My Seller Finance"
        subtitle="Track what has sold, what is eligible, and what has already been paid out."
      >
        <FinanceSectionCard title="No finance data found">
          <Text>
            No seller finance data was found for your account yet. Once you have
            sales or allocations, they will appear here.
          </Text>
        </FinanceSectionCard>
      </FinancePageShell>
    );
  }

  const balance = payload?.balance || {};

  return (
    <FinancePageShell
      title="My Seller Finance"
      subtitle="Track what has sold, what is eligible, and what has already been paid out."
    >
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
        <FinanceStatCard
          label="Gross Sales"
          value={formatMoney(balance.grossSales)}
        />
        <FinanceStatCard
          label="Pending"
          value={formatMoney(balance.pendingPayable)}
        />
        <FinanceStatCard
          label="Eligible"
          value={formatMoney(balance.eligiblePayable)}
        />
        <FinanceStatCard
          label="Paid Out"
          value={formatMoney(balance.paidOutAmount)}
        />
        <FinanceStatCard
          label="Platform Fees"
          value={formatMoney(balance.platformFees)}
        />
        <FinanceStatCard
          label="Refunded"
          value={formatMoney(balance.refundedAmount)}
        />
      </SimpleGrid>

      <FinanceSectionCard title="This page uses your signed-in user id">
        It connects directly to the seller finance endpoint and shows your
        finance balance without changing your existing shop/product flows.
      </FinanceSectionCard>
    </FinancePageShell>
  );
};

export default SellerFinanceDashboard;